import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

/**
 * Playback state of a voice message in the browser, shared by the client-web and admin chat
 * players so both behave the same: a lazily created `<audio>` (nothing is fetched until the first
 * play), one message playing at a time, a smooth progress value while playing, and the speed
 * cycle every messenger has for long recordings.
 */

export const AUDIO_PLAYBACK_RATES = [1, 1.5, 2] as const;
export type AudioPlaybackRate = (typeof AUDIO_PLAYBACK_RATES)[number];

interface UseAudioPlayerOptions {
  src: string;
  /**
   * Length stored on the attachment, in seconds. Used until the browser reports one, and instead
   * of it for a MediaRecorder WebM, whose header says the track is infinitely long.
   */
  fallbackDurationSeconds?: number | null;
}

export interface AudioPlayerControls {
  isPlaying: boolean;
  /** Seconds. */
  currentTime: number;
  /** Seconds; 0 while unknown. */
  duration: number;
  /** 0..1 of `duration`. */
  progress: number;
  playbackRate: AudioPlaybackRate;
  toggle: () => void;
  /** Jump to a point of the track, given as 0..1. */
  seek: (fraction: number) => void;
  cyclePlaybackRate: () => void;
}

// Whichever element is playing right now; starting another one pauses it.
let playingElement: HTMLAudioElement | null = null;

const isUsableDuration = (value: number): boolean => Number.isFinite(value) && value > 0;

export function useAudioPlayer({ src, fallbackDurationSeconds }: UseAudioPlayerOptions): AudioPlayerControls {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [reportedDuration, setReportedDuration] = useState<number | null>(null);
  const [playbackRate, setPlaybackRate] = useState<AudioPlaybackRate>(AUDIO_PLAYBACK_RATES[0]);
  const playbackRateRef = useRef(playbackRate);
  playbackRateRef.current = playbackRate;

  const duration = reportedDuration ?? (isUsableDuration(fallbackDurationSeconds ?? 0) ? fallbackDurationSeconds! : 0);

  const getAudio = useCallback((): HTMLAudioElement => {
    if (audioRef.current) {
      return audioRef.current;
    }

    const audio = new Audio();
    audio.preload = "none";
    audio.src = src;

    audio.addEventListener("play", () => {
      if (playingElement && playingElement !== audio) {
        playingElement.pause();
      }
      playingElement = audio;
      setIsPlaying(true);
    });
    audio.addEventListener("pause", () => setIsPlaying(false));
    audio.addEventListener("ended", () => {
      audio.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(false);
    });
    audio.addEventListener("timeupdate", () => setCurrentTime(audio.currentTime));
    audio.addEventListener("durationchange", () => {
      if (isUsableDuration(audio.duration)) {
        setReportedDuration(audio.duration);
      }
    });
    // A stalled or half-fetched source (tab left open for hours, flaky network) fails the first
    // play; one fresh load gives it a second chance before the button silently does nothing.
    let retried = false;
    audio.addEventListener("error", () => {
      setIsPlaying(false);
      if (retried) {
        return;
      }
      retried = true;
      audio.load();
      void audio.play().catch(() => setIsPlaying(false));
    });

    audioRef.current = audio;
    return audio;
  }, [src]);

  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (!audio) {
        return;
      }
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      if (playingElement === audio) {
        playingElement = null;
      }
      audioRef.current = null;
    };
  }, [src]);

  // `timeupdate` ticks about four times a second — too coarse for the waveform to sweep
  // smoothly, so the position is sampled per frame while something is actually playing.
  useEffect(() => {
    if (!isPlaying) {
      return undefined;
    }
    let frame = 0;
    const tick = () => {
      const audio = audioRef.current;
      if (audio) {
        setCurrentTime(audio.currentTime);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isPlaying]);

  const toggle = useCallback(() => {
    const audio = getAudio();
    if (audio.paused) {
      audio.playbackRate = playbackRateRef.current;
      void audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  }, [getAudio]);

  const seek = useCallback(
    (fraction: number) => {
      if (!isUsableDuration(duration)) {
        return;
      }
      const audio = getAudio();
      const time = Math.min(Math.max(fraction, 0), 1) * duration;
      audio.currentTime = time;
      setCurrentTime(time);
    },
    [duration, getAudio],
  );

  const cyclePlaybackRate = useCallback(() => {
    const index = AUDIO_PLAYBACK_RATES.indexOf(playbackRateRef.current);
    const next = AUDIO_PLAYBACK_RATES[(index + 1) % AUDIO_PLAYBACK_RATES.length]!;
    setPlaybackRate(next);
    if (audioRef.current) {
      audioRef.current.playbackRate = next;
    }
  }, []);

  return {
    isPlaying,
    currentTime,
    duration,
    progress: isUsableDuration(duration) ? Math.min(currentTime / duration, 1) : 0,
    playbackRate,
    toggle,
    seek,
    cyclePlaybackRate,
  };
}

const KEYBOARD_SEEK_STEP = 0.05;

/**
 * Pointer and keyboard handlers that turn any element into a scrub bar: press or drag anywhere
 * on it to jump there, arrow keys to nudge. Spread onto the waveform container.
 */
export function useAudioSeekGestures({ progress, seek }: Pick<AudioPlayerControls, "progress" | "seek">) {
  const dragging = useRef(false);

  const fractionAt = (event: PointerEvent<HTMLElement>): number => {
    const rect = event.currentTarget.getBoundingClientRect();
    return rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0;
  };

  return {
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      dragging.current = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      seek(fractionAt(event));
    },
    onPointerMove: (event: PointerEvent<HTMLElement>) => {
      if (dragging.current) {
        seek(fractionAt(event));
      }
    },
    onPointerUp: (event: PointerEvent<HTMLElement>) => {
      dragging.current = false;
      event.currentTarget.releasePointerCapture(event.pointerId);
    },
    onPointerCancel: () => {
      dragging.current = false;
    },
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        seek(progress + KEYBOARD_SEEK_STEP);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        seek(progress - KEYBOARD_SEEK_STEP);
      }
    },
  };
}
